import type { Identifier } from "ra-core";

import type {
  LeadAssignmentChannel,
  LeadAssignmentChannelInput,
  LeadAssignmentEligibleAdvisor,
  LeadAssignmentRule,
  LeadAssignmentRuleInput,
} from "../../types";
import { getSupabaseClient } from "./supabase";

/**
 * Almikia Epic EL (TASK-008): Supabase-side writes of the "Asignacion de leads" screen.
 * `lead_assignment_channels` (PK `code`) and `lead_assignment_eligible_advisors` (composite
 * PK) have no `id` column, so the generic ra-data-postgrest update cannot address them:
 * these methods upsert on the real key. RLS keeps every write direccion-only.
 */
export const createSupabaseLeadAssignmentConfigMethods = () => ({
  async getLeadAssignmentChannels(): Promise<LeadAssignmentChannel[]> {
    const { data, error } = await getSupabaseClient()
      .from("lead_assignment_channels")
      .select("*")
      .order("code");
    if (error) throw new Error(error.message);
    // Primary key is `code`: reuse it as the synthetic `id` react-admin requires.
    return (data as Omit<LeadAssignmentChannel, "id">[]).map((row) => ({
      ...row,
      id: row.code,
    }));
  },

  async saveLeadAssignmentRule(
    input: LeadAssignmentRuleInput,
  ): Promise<LeadAssignmentRule> {
    const { data, error } = await getSupabaseClient()
      .from("lead_assignment_rules")
      .upsert(
        { ...input, updated_at: new Date().toISOString() },
        { onConflict: "project_id" },
      )
      .select()
      .single();
    if (error || !data) {
      throw new Error(error?.message || "No se pudo guardar la regla");
    }
    return data as LeadAssignmentRule;
  },

  /**
   * Epic EL2: the cap and 'vencido' threshold of one project, through the SECURITY DEFINER
   * `set_project_assignment_limits` (a Team Leader may not write `lead_assignment_rules`
   * directly). null = no limit; `is_enabled` is never touched.
   */
  async setProjectAssignmentLimits(
    projectId: Identifier,
    cap: number | null,
    overdueHours: number | null,
  ): Promise<LeadAssignmentRule> {
    const { data, error } = await getSupabaseClient().rpc(
      "set_project_assignment_limits",
      { p_project_id: projectId, p_cap: cap, p_overdue_hours: overdueHours },
    );
    if (error || !data) {
      throw new Error(error?.message || "No se pudieron guardar los límites");
    }
    return data as LeadAssignmentRule;
  },

  async setLeadAssignmentEligibility(
    projectId: Identifier,
    salesId: Identifier,
    isEligible: boolean,
  ): Promise<LeadAssignmentEligibleAdvisor> {
    const { data, error } = await getSupabaseClient()
      .from("lead_assignment_eligible_advisors")
      .upsert(
        { project_id: projectId, sales_id: salesId, is_eligible: isEligible },
        { onConflict: "project_id,sales_id" },
      )
      .select()
      .single();
    if (error || !data) {
      throw new Error(error?.message || "No se pudo guardar la participación");
    }
    const row = data as Omit<LeadAssignmentEligibleAdvisor, "id">;
    return { ...row, id: `${row.project_id}-${row.sales_id}` };
  },

  async saveLeadAssignmentChannel(
    input: LeadAssignmentChannelInput,
  ): Promise<LeadAssignmentChannel> {
    const { data, error } = await getSupabaseClient()
      .from("lead_assignment_channels")
      .upsert(input, { onConflict: "code" })
      .select()
      .single();
    if (error || !data) {
      throw new Error(error?.message || "No se pudo guardar el canal");
    }
    const row = data as Omit<LeadAssignmentChannel, "id">;
    return { ...row, id: row.code };
  },
});
